import React, { useState, useEffect } from "react"
import MetaTags from "react-meta-tags"
import { Container, Row, Col, Card, CardBody } from "reactstrap"
import moment from "moment"
import { connect } from "react-redux"
import _ from "lodash"
import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import ReactApexChart from "react-apexcharts"

import * as backend from "../../helpers/backend_helper"
import * as func from "../../common/functions"

const TEAL = "#2ea99b"
const NAVY = "#1D3A5F"

// Colour a 0-100 compliance/performance score.
const scoreColor = v => {
  const n = Math.round(Number(v) || 0)
  if (n >= 80) return { fg: "#1c8577", bg: "#e5f6f2", bd: "#2ea99b" }
  if (n >= 50) return { fg: "#b7791f", bg: "#fdf3e3", bd: "#e0a83c" }
  return { fg: "#c0392b", bg: "#fdecec", bd: "#e07a6f" }
}

// A thin radial gauge (KPI-dashboard style) — light track, rounded teal arc,
// big number centred. Colour follows the score (green / amber / red).
const GaugeCard = ({ title, subtitle, value }) => {
  const n = Math.round(Number(value) || 0)
  const c = scoreColor(n)
  const options = {
    chart: { sparkline: { enabled: true }, offsetY: 0 },
    colors: [c.bd],
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: { size: "68%" },
        track: { background: "#eef1f4", strokeWidth: "100%", margin: 0 },
        dataLabels: {
          name: { show: false },
          value: {
            offsetY: 8,
            fontSize: "30px",
            fontWeight: 700,
            color: NAVY,
            formatter: val => `${Math.round(val)}`,
          },
        },
      },
    },
    stroke: { lineCap: "round" },
    fill: {
      type: "gradient",
      gradient: { shade: "light", type: "horizontal", gradientToColors: [c.fg], stops: [0, 100] },
    },
  }
  return (
    <Card style={{ height: "100%" }}>
      <CardBody style={{ textAlign: "center", padding: "16px 12px 8px" }}>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "#7a8794", textTransform: "uppercase", letterSpacing: "0.6px" }}>
          {title}
        </div>
        <ReactApexChart options={options} series={[n]} type="radialBar" height={170} />
        {subtitle && <div style={{ fontSize: "12px", color: "#9aa6b2", marginTop: "8px" }}>{subtitle}</div>}
      </CardBody>
    </Card>
  )
}

// A titled chart card — the shell every chart panel sits in.
const ChartCard = ({ title, right, children, height }) => (
  <Card style={{ height: "100%" }}>
    <CardBody>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <h6 style={{ margin: 0, color: NAVY, fontWeight: 600 }}>{title}</h6>
        {right && <span style={{ marginLeft: "auto", fontSize: 12, color: "#9aa6b2" }}>{right}</span>}
      </div>
      <div style={{ minHeight: height || 260 }}>{children}</div>
    </CardBody>
  </Card>
)

// Line chart: daily compliance score across the selected range.
const TrendLineChart = ({ points }) => {
  const options = {
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    colors: [TEAL],
    stroke: { curve: "smooth", width: 3 },
    markers: { size: 4, colors: ["#fff"], strokeColors: TEAL, strokeWidth: 2, hover: { size: 6 } },
    dataLabels: { enabled: false },
    grid: { borderColor: "#eef1f4", strokeDashArray: 4 },
    xaxis: {
      categories: points.map(p => p.label),
      labels: { style: { colors: "#9aa6b2", fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { min: 0, max: 100, tickAmount: 5, labels: { style: { colors: "#9aa6b2" } } },
    tooltip: { y: { formatter: val => `${val} / 100` } },
  }
  const series = [{ name: "Compliance", data: points.map(p => p.value) }]
  return <ReactApexChart options={options} series={series} type="line" height={260} />
}

// Horizontal bar chart of per-department compliance scores.
const DepartmentBarChart = ({ rows }) => {
  const options = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%", distributed: true } },
    colors: (rows || []).map(r => scoreColor(r.value).bd),
    dataLabels: { enabled: true, formatter: val => `${val}`, style: { colors: ["#31435a"], fontWeight: 700 }, offsetX: 16 },
    legend: { show: false },
    grid: { borderColor: "#eef1f4", strokeDashArray: 4 },
    xaxis: { max: 100, categories: (rows || []).map(r => r.name), labels: { style: { colors: "#9aa6b2" } } },
    yaxis: { labels: { style: { colors: "#31435a", fontWeight: 600 } } },
    tooltip: { y: { formatter: val => `${val} / 100` } },
  }
  const series = [{ name: "Compliance", data: (rows || []).map(r => r.value) }]
  return <ReactApexChart options={options} series={series} type="bar" height={Math.max(220, (rows || []).length * 46)} />
}

// Area chart: team performance score month-by-month across the year.
const MonthlyAreaChart = ({ points }) => {
  const options = {
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    colors: [NAVY],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } },
    dataLabels: { enabled: false },
    grid: { borderColor: "#eef1f4", strokeDashArray: 4 },
    xaxis: {
      categories: points.map(p => p.label),
      labels: { style: { colors: "#9aa6b2", fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { min: 0, max: 100, tickAmount: 5, labels: { style: { colors: "#9aa6b2" } } },
    tooltip: { y: { formatter: val => `${val} / 100` } },
  }
  const series = [{ name: "Team performance", data: points.map(p => p.value) }]
  return <ReactApexChart options={options} series={series} type="area" height={260} />
}

const Dashboard = props => {
  const { selectedFactory } = props
  // Non-admins see only their own score (matches the Performance Score page).
  const isAdminViewer =
    !!(props.user && props.user.superAdmin) ||
    !!(selectedFactory && selectedFactory.permission && selectedFactory.permission.includes("ADMIN"))
  // Default range: first day of the current month → today.
  const [range, setRange] = useState([
    moment().startOf("month").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ])
  const [userList, setUserList] = useState([])
  const [reportList, setReportList] = useState([]) // daily monitoring reports for the range
  const [yearReportList, setYearReportList] = useState([]) // daily reports across the whole year (for trend)
  const [score, setScore] = useState() // monthly monitoring score object
  const [performanceReport, setPerformanceReport] = useState()
  const [totalQuestionInDepart, setTotalQuestionInDepart] = useState()
  const [carryDocs, setCarryDocs] = useState({}) // latest Document Review state per point
  const [monthlyReportRaw, setMonthlyReportRaw] = useState([]) // monthly work report items
  const [submissionScore, setSubmissionScore] = useState({}) // per-user 0–100 report-submission score

  const fromDate = range[0]
  const toDate = range[1]
  const year = moment(toDate, "YYYY-MM-DD").format("YYYY")
  // Months (short names) the range covers, for the monthly-based team score.
  const rangeMonths = []
  {
    let m = moment(fromDate, "YYYY-MM-DD").startOf("month")
    const end = moment(toDate, "YYYY-MM-DD").endOf("month")
    while (m.isSameOrBefore(end)) {
      rangeMonths.push(m.format("MMM"))
      m = m.add(1, "month")
    }
  }

  // Document Review state (for expiry widgets) as of the range end.
  useEffect(() => {
    if (!selectedFactory) return
    const { orgId, _id: facId } = selectedFactory
    backend
      .getSatutoryReportCarry(orgId, facId, moment(toDate, "YYYY-MM-DD").format("YYYY-MM"))
      .then(m => setCarryDocs(m || {}))
      .catch(() => setCarryDocs({}))
  }, [selectedFactory && selectedFactory._id, toDate])

  useEffect(() => {
    if (!selectedFactory) return
    const { orgId, _id: facId, teams } = selectedFactory

    setTotalQuestionInDepart(
      func.getTotalQuestionsindailyMonitoringReport(
        selectedFactory.dailyMonitoringForm,
        selectedFactory.departments
      )
    )

    backend.getUserOrg(orgId).then(setUserList).catch(() => {})

    // Daily monitoring reports across the selected date range.
    backend
      .getdailymonitoringMonthReport(orgId, facId, fromDate, toDate)
      .then(r => setReportList(r || []))
      .catch(() => {})

    // Per-user report-submission score (0–100) — same source as Performance Score.
    backend
      .getReportSubmissionScore(orgId, facId, fromDate, toDate)
      .then(r => setSubmissionScore(r || {}))
      .catch(() => {})

    // Daily monitoring reports across the whole year (for the compliance trend line).
    backend
      .getdailymonitoringMonthReport(orgId, facId, `${year}-01-01`, `${year}-12-31`)
      .then(r => setYearReportList(r || []))
      .catch(() => {})

    backend
      .getmonthlyMonitoringReport(orgId, facId, year)
      .then(res => {
        if (res && res.report) {
          setMonthlyReportRaw(res.report)
          setScore(
            func.monthlyMonitoringScoreCalc(
              selectedFactory.monthlyMonitoringForm,
              selectedFactory.responsibility,
              selectedFactory.teams,
              res.report,
              year
            )
          )
        }
      })
      .catch(() => {})

    backend
      .getmonthlyperformaceReport(orgId, facId, `${year}-01-01`, `${year}-12-31`)
      .then(res => {
        if (res) setPerformanceReport(func.performanceReportMonthly(res, teams, year))
      })
      .catch(() => {})
  }, [selectedFactory && selectedFactory._id, fromDate, toDate, year])

  // Overall compliance score for a single day's report array (avg of dept scores).
  const dayScore = rep => {
    if (!selectedFactory || !rep) return 0
    const vals = (selectedFactory.departments || [])
      .map(depart => {
        const total =
          totalQuestionInDepart && totalQuestionInDepart[depart._id]
            ? totalQuestionInDepart[depart._id].total
            : 0
        if (!total) return null
        const sel = _.filter(rep, { departmentId: depart._id, type: "select" })
          .map(f => (f.value ? Number(f.value.split(":")[1]) : 0))
          .filter(o => o === 1).length
        return Math.round((sel / total) * 100)
      })
      .filter(x => x !== null)
    return vals.length ? Math.round(_.sumBy(vals) / vals.length) : 0
  }

  // ---- Compliance trend: one point per submitted day in the range ----
  const complianceTrend = () => {
    const inRange = _.filter(yearReportList, o =>
      moment(o.date, "YYYY-MM-DD").isBetween(fromDate, toDate, undefined, "[]")
    )
    return _.sortBy(inRange, "date")
      .filter(d => !d.remarks) // skip holidays / non-working days
      .map(d => ({ label: moment(d.date).format("DD MMM"), value: dayScore(d.report) }))
  }

  // ---- Departments compliance (per-department daily monitoring over the range) ----
  // Department compliance — identical to the Performance Score page: scored from
  // the questions ACTUALLY RECORDED (immune to later question edits), excluding
  // rejected reports.
  const departmentScores = () => {
    if (!selectedFactory) return { rows: [], avg: 0 }
    const rangeReports = _.filter(reportList, o =>
      moment(o.date, "YYYY-MM-DD").isBetween(fromDate, toDate, undefined, "[]") &&
      !(o.validation && o.validation.status === "rejected")
    )
    const rows = (selectedFactory.departments || []).map(depart => {
      let value = 0
      if (rangeReports.length > 0) {
        const report = [].concat.apply([], _.map(rangeReports, "report"))
        const answered = _.filter(report, { departmentId: depart._id, type: "select" })
          .filter(f => f.value)
        const yes = answered.filter(f => Number(String(f.value).split(":")[1]) === 1)
        const byValue = answered.length
        value = byValue ? Math.round((yes.length / byValue) * 100) : 0
      }
      return { name: depart.name, value }
    })
    const avg = rows.length ? Math.round(_.sumBy(rows, "value") / rows.length) : 0
    return { rows, avg }
  }

  // Team performance average for a single month label (e.g. "Aug").
  const teamScoreForMonth = mLabel => {
    if (!selectedFactory || !selectedFactory.teams) return 0
    let total = 0
    let count = 0
    selectedFactory.teams.forEach(team => {
      let value = 0
      if (score && performanceReport) {
        const user = score[team.userId]
        const preport = performanceReport[team.userId]
        if (user) {
          value += user[mLabel] ? user[mLabel] : 0
          const mKey = `${year}-${moment().month(mLabel).format("MM")}`
          value += preport && preport[mKey] ? preport[mKey] : 0
        }
      }
      total += value
      count += 1
    })
    return count ? Math.round(total / count) : 0
  }

  // ---- Team performance (avg over the months in range) ----
  // Team Performance Score — identical to the Performance Score page: the average
  // (0–100) submission score across active members who have a report assigned.
  const teamScore = () => {
    if (!selectedFactory || !selectedFactory.teams) return 0
    const assignedUserIds = new Set(
      ((selectedFactory.reportScoring) || [])
        .filter(e => (e.items || []).some(it => Number(it.score) > 0))
        .map(e => String(e.userId))
    )
    // "Average across team" — the team average for every viewer (admin or member),
    // not the individual's own score.
    const vals = selectedFactory.teams
      .filter(t => t.status !== false)
      .filter(t => assignedUserIds.has(String(t.userId)))
      .map(t => Number(submissionScore && submissionScore[t.userId]) || 0)
    return vals.length ? Math.round(_.sum(vals) / vals.length) : 0
  }

  // ---- Team performance trend (Jan → Dec of the range's year) ----
  const teamMonthlyTrend = () =>
    moment.monthsShort().map(mLabel => ({ label: mLabel, value: teamScoreForMonth(mLabel) }))

  // ---- Document Review expiry (from the Monthly Document Review) ----
  const documentBuckets = () => {
    const expiring = []
    const expired = []
    const today = moment().startOf("day")
    const soon = moment().add(30, "days").endOf("day")
    Object.keys(carryDocs || {}).forEach(key => {
      const d = carryDocs[key]
      if (!d || !d.expDate) return
      const name = key.split("::").pop()
      const exp = moment(d.expDate, "YYYY-MM-DD")
      if (!exp.isValid()) return
      if (exp.isBefore(today)) {
        expired.push({ name, expDate: d.expDate })
      } else if (exp.isSameOrBefore(soon)) {
        expiring.push({ name, expDate: d.expDate })
      }
    })
    const byDate = (a, b) => (a.expDate < b.expDate ? -1 : 1)
    return { expiring: expiring.sort(byDate), expired: expired.sort(byDate) }
  }

  // ---- Overdue Monthly Work Report tasks (target date passed, not yet done) ----
  const overdueTasks = () => {
    if (!selectedFactory || !selectedFactory.monthlyMonitoringForm) return []
    const out = []
    const now = moment()
    selectedFactory.monthlyMonitoringForm.forEach(group => {
      ;(group.lists || []).forEach(point => {
        const day = parseInt(point.date, 10)
        if (!day) return
        const applicableMonths = _.map(point.months || [], "value") // e.g. ["Jan","Feb"]
        applicableMonths.forEach(mShort => {
          const mNum = moment().month(mShort).format("MM")
          const due = moment(`${year}-${mNum}-${String(day).padStart(2, "0")}`, "YYYY-MM-DD")
          if (!due.isValid() || due.isAfter(now)) return // not due yet
          // Only surface tasks that became overdue within the last 30 days —
          // older lapsed tasks drop off the dashboard list.
          if (due.isBefore(moment(now).subtract(30, "days"))) return
          const done = _.some(
            monthlyReportRaw,
            r =>
              r.groupId === group._id &&
              r.questionId === point._id &&
              r.month === mShort &&
              r.value
          )
          if (!done) {
            out.push({ name: point.name, month: mShort, due: due.format("YYYY-MM-DD") })
          }
        })
      })
    })
    return _.sortBy(out, "due")
  }

  if (!selectedFactory) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="mw-page-header">
            <div className="mw-page-title">
              <h4>Dashboard</h4>
              <p>Select a factory to view its compliance overview.</p>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  const depart = departmentScores()
  const factoryCompliance = depart.avg // Factory compliance = avg of department daily scores
  const team = teamScore()
  const docs = documentBuckets()
  const overdue = overdueTasks()
  const trend = complianceTrend()
  const monthly = teamMonthlyTrend()
  // Only factory admins see the full dashboard (charts, document & task lists);
  // everyone else sees just the headline KPI gauges.
  const isAdmin = !!(selectedFactory && selectedFactory.permission && selectedFactory.permission.includes("ADMIN"))

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Dashboard | Monitorwise</title>
        </MetaTags>
        <Container fluid>
          <div className="mw-page-header">
            <div className="mw-page-title">
              <h4>Dashboard</h4>
              <p>{selectedFactory.name} — compliance overview for {fromDate} to {toDate}</p>
            </div>
            <div style={{ width: 260 }}>
              <Flatpickr
                className="form-control d-block"
                value={range}
                options={{ mode: "range", dateFormat: "Y-m-d" }}
                onChange={dates => {
                  if (dates.length === 2) {
                    setRange([
                      moment(dates[0]).format("YYYY-MM-DD"),
                      moment(dates[1]).format("YYYY-MM-DD"),
                    ])
                  }
                }}
              />
            </div>
          </div>

          {/* Headline KPI gauges */}
          <Row>
            <Col md={4} className="mb-3">
              <GaugeCard title="Factory Compliance Score" subtitle="From daily monitoring" value={factoryCompliance} />
            </Col>
            <Col md={4} className="mb-3">
              <GaugeCard title="Team Performance Score" subtitle="Average across team" value={team} />
            </Col>
            <Col md={4} className="mb-3">
              <GaugeCard title="Departments Compliance Score" subtitle="Average across departments" value={depart.avg} />
            </Col>
          </Row>

          {isAdmin && (
          <React.Fragment>
          {/* Trend line + department bar */}
          <Row>
            <Col lg={7} className="mb-3">
              <ChartCard title="Compliance Trend" right={`${fromDate} → ${toDate}`}>
                {trend.length > 0 ? (
                  <TrendLineChart points={trend} />
                ) : (
                  <p className="text-muted" style={{ fontSize: 13 }}>No daily monitoring in this range.</p>
                )}
              </ChartCard>
            </Col>
            <Col lg={5} className="mb-3">
              <ChartCard title="Department Compliance">
                {depart.rows.length > 0 ? (
                  <DepartmentBarChart rows={depart.rows} />
                ) : (
                  <p className="text-muted" style={{ fontSize: 13 }}>No departments configured.</p>
                )}
              </ChartCard>
            </Col>
          </Row>

          {/* Document / task watch-lists */}
          <Row>
            <Col md={4} className="mb-3">
              <ListCard
                title="Expiring in 30 days"
                icon="bx bx-time-five"
                accent="#b7791f"
                rows={docs.expiring}
                render={r => (
                  <React.Fragment>
                    <span style={{ flex: 1, minWidth: 0 }}>{r.name}</span>
                    <span style={{ color: "#b7791f", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>{r.expDate}</span>
                  </React.Fragment>
                )}
                empty="No documents expiring soon"
              />
            </Col>
            <Col md={4} className="mb-3">
              <ListCard
                title="Expired documents"
                icon="bx bx-error-circle"
                accent="#c0392b"
                rows={docs.expired}
                render={r => (
                  <React.Fragment>
                    <span style={{ flex: 1, minWidth: 0 }}>{r.name}</span>
                    <span style={{ color: "#c0392b", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>{r.expDate}</span>
                  </React.Fragment>
                )}
                empty="No expired documents"
              />
            </Col>
            <Col md={4} className="mb-3">
              <ListCard
                title="Overdue monthly tasks"
                icon="bx bx-list-check"
                accent="#c0392b"
                rows={overdue}
                render={r => (
                  <React.Fragment>
                    <span style={{ flex: 1, minWidth: 0 }}>{r.name} <small className="text-muted">({r.month})</small></span>
                    <span style={{ color: "#c0392b", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>{r.due}</span>
                  </React.Fragment>
                )}
                empty="No overdue tasks"
              />
            </Col>
          </Row>
          </React.Fragment>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

// A compact card listing rows (documents / tasks) with a coloured header.
const ListCard = ({ title, icon, accent, rows, render, empty }) => (
  <Card style={{ height: "100%" }}>
    <CardBody>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <i className={icon} style={{ fontSize: 20, color: accent }} />
        <h6 style={{ margin: 0, color: "#1D3A5F" }}>{title}</h6>
        <span
          style={{
            marginLeft: "auto",
            fontWeight: 700,
            color: accent,
            background: "#f4f6f9",
            borderRadius: 12,
            padding: "1px 10px",
            fontSize: 13,
          }}
        >
          {rows.length}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted mb-0" style={{ fontSize: 13 }}>{empty}</p>
      ) : (
        <ul className="list-unstyled mb-0" style={{ maxHeight: 220, overflowY: "auto" }}>
          {rows.map((r, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "6px 0",
                borderBottom: "1px solid #eef1f4",
                fontSize: 13,
              }}
            >
              {render(r)}
            </li>
          ))}
        </ul>
      )}
    </CardBody>
  </Card>
)

const mapStateToProps = state => {
  const { selected: selectedFactory } = state.factory
  const { user } = state.user
  return { selectedFactory, user }
}

export default connect(mapStateToProps, {})(Dashboard)
